import React, { useEffect, useRef, useState } from "react";
import { ArrowLeft, ChevronLeftIcon, ChevronRightIcon, Package } from "lucide-react";
import ProductSearchList from "../../../../../components/product-selector/_components/product-search-list";
import ProductComplementsSelect from "../../../../../components/product-selector/_components/product-complements-select";
import { calcularPrecoProduto } from "../../../../../components/product-selector/_utils/product-rules";
import libFloat from "../../../../../util/libFloat";
import Notification from "../../../../../components/toast";

// Busca + seleção de complementos, reaproveitando o seletor de produto global.
// Só entrega pro chamador (StepProdutos) um par { item, complementos } prontos no formato
// que a tabela de "Produtos / Itens" já espera — não sabe nada sobre venda_item por si só.
export default function AddProductFlow({ onAdd, onCancel }) {
	const [produto, setProduto] = useState(null);
	const [quantidade, setQuantidade] = useState(1);
	const [complementosSelecionados, setComplementosSelecionados] = useState([]);
	const [complementosValidos, setComplementosValidos] = useState(true);
	const [hasSubitem, setHasSubitem] = useState(false);
	const [hasGrade, setHasGrade] = useState(false);
	const qtyInputRef = useRef(null);
	const addButtonRef = useRef(null);

	// Ao escolher o produto (e enquanto o seletor de quantidade estiver visível, ou seja, sem
	// grade), o foco vai direto pro input — fluxo pensado pra ser operado só pelo teclado.
	useEffect(() => {
		if (produto && !hasGrade) {
			qtyInputRef.current?.focus();
			qtyInputRef.current?.select();
		}
	}, [produto, hasGrade]);

	const handleSelectProduct = (p) => {
		setProduto(p);
		setQuantidade(1);
		setComplementosSelecionados([]);
		setComplementosValidos(true);
		setHasSubitem(false);
		setHasGrade(false);
	};

	const handleIncrementQtde = () => setQuantidade((prev) => prev + 1);
	const handleDecrementQtde = () => setQuantidade((prev) => Math.max(1, prev - 1));
	const handleQuantidadeChange = (value) => {
		if (value === "") {
			setQuantidade("");
			return;
		}
		const n = parseInt(value, 10);
		if (!isNaN(n) && n >= 0) setQuantidade(n);
	};
	const handleQuantidadeBlur = () => {
		if (quantidade === "" || quantidade < 1) setQuantidade(1);
	};

	// Enter na quantidade já confirma e manda o foco direto pro botão de adicionar — aqui os
	// complementos são opcionais, então não faz sentido forçar navegação por eles no teclado.
	const handleQuantidadeKeyDown = (e) => {
		if (e.key === "Enter") {
			e.preventDefault();
			addButtonRef.current?.focus();
		}
	};

	const handleAdicionar = () => {
		if (!complementosValidos) {
			Notification.showToast({ message: "Selecione os complementos obrigatórios.", type: "warning" });
			return;
		}
		if (hasSubitem && !complementosSelecionados.some((c) => c.subitem)) {
			Notification.showToast({ message: "Selecione ao menos uma opção (sabor/tamanho).", type: "warning" });
			return;
		}

		const { valorOriginal, valorFinal, descontoUnitario, complementosFinais } = calcularPrecoProduto({
			valorTabela: produto.valor,
			valorPromo: produto.valorpromoapi || 0,
			complementosSelecionados,
			hasGrade,
		});

		if (valorFinal <= 0) {
			Notification.showToast({ message: "Selecione os complementos do produto.", type: "warning" });
			return;
		}

		// Id local temporário — vira id_venda_item de verdade só quando gravar no backend
		const tempId = `novo_${Date.now()}`;
		// Grade nunca agrega quantidade numa linha só (cada seleção é seu próprio SKU/linha,
		// mesma regra anti-agregação do sz-foods) — o seletor de qtde fica escondido pra grade.
		const qtdeFinal = hasGrade ? 1 : Number(quantidade) || 1;

		// valor é só a contribuição do PAI pra linha (mesma regra do sz-foods: Grade zera aqui
		// e o valor vive inteiro no complemento; Subitem consolida aqui e o complemento vai
		// zerado; Normal fica com o preço de tabela e o Adicional soma por fora). Quem soma
		// pai + complementos pra achar o total da linha é a tela (calcularTotalLinha em
		// step-produtos.jsx) — não pode ficar embutido aqui, senão conta em dobro.
		const novoItem = {
			id_venda_item: tempId,
			id_produto: produto.id_produto,
			nome: produto.nome,
			qtde: qtdeFinal,
			valor_unitario: valorOriginal,
			valor: valorOriginal * qtdeFinal,
			desconto: descontoUnitario * qtdeFinal,
			grade: hasGrade ? complementosFinais.find((c) => c.is_grade)?.nome_produto_item || null : null,
			_novo: true,
		};

		// Idem, seguindo as colunas reais de VENDA_COMPLEMENTOS: id_produto é a FK obrigatória
		// (se perdia no mapeamento antes), e a tabela real tem uma única coluna TIPO (string),
		// não os dois booleans subitem/is_grade que só existem no lado do sz-foods.
		const novosComplementos = complementosFinais.map((c) => ({
			id_venda_item: tempId,
			id_produto: c.id_produto,
			nome: c.nome_produto_item,
			qtde: c.quantidade,
			valorunit: c.valor_item,
			valor: c.valor_total,
			tipo: c.is_grade ? "grade" : c.subitem ? "subitem" : "adicional",
			_novo: true,
		}));

		onAdd(novoItem, novosComplementos);
	};

	if (!produto) {
		return (
			<div className="flex h-full flex-col space-y-4">
				<div className="flex flex-shrink-0 items-center justify-between border-b border-gray-100 pb-3">
					<div className="flex items-center gap-3">
						<button
							type="button"
							onClick={onCancel}
							className="flex h-9 w-9 items-center justify-center rounded-xl border border-gray-200 bg-white text-gray-500 shadow-sm transition-all hover:border-gray-300 hover:bg-gray-50 hover:text-gray-800 active:scale-95"
							title="Voltar para a lista de itens"
						>
							<ArrowLeft size={18} />
						</button>
						<div>
							<h3 className="text-sm font-black text-gray-800 uppercase tracking-tight">Buscar produto pra adicionar</h3>
							<p className="text-xs text-gray-500 font-medium">Selecione o produto no catálogo para incluir na alteração</p>
						</div>
					</div>
					<button
						type="button"
						onClick={onCancel}
						className="px-3 py-1.5 rounded-xl border border-gray-200 text-xs font-bold text-gray-600 hover:bg-gray-50 transition-all"
					>
						Voltar à Tabela
					</button>
				</div>
				<div className="min-h-0 flex-1 overflow-hidden">
					<ProductSearchList onSelectProduct={handleSelectProduct} />
				</div>
			</div>
		);
	}

	return (
		<div className="flex h-full flex-col space-y-3">
			<div className="flex flex-shrink-0 items-center justify-between border-b border-gray-100 pb-2">
				<button
					type="button"
					onClick={() => setProduto(null)}
					className="flex items-center gap-2 rounded-xl bg-gray-100 px-3 py-1.5 text-xs font-bold text-gray-700 transition-all hover:bg-gray-200 active:scale-95"
				>
					<ArrowLeft size={14} /> Trocar produto
				</button>
				<span className="text-[11px] font-extrabold uppercase tracking-wider text-blue-600 bg-blue-50 px-2.5 py-1 rounded-full border border-blue-100">
					Configuração de Complementos
				</span>
			</div>

			{/* Cabeçalho: nome do produto em destaque, pra nunca ficar em dúvida do que está sendo configurado */}
			<form
				onSubmit={(e) => e.preventDefault()}
				className="mb-3 flex flex-shrink-0 items-center justify-between gap-3 rounded-2xl border border-blue-100 bg-blue-50/60 p-4"
			>
				<div className="flex min-w-0 items-center gap-3">
					<div className="flex-shrink-0 rounded-xl bg-blue-600 p-2.5 text-white">
						<Package size={22} />
					</div>
					<div className="min-w-0">
						<h2 className="truncate text-lg font-black leading-tight text-gray-900">{produto.nome}</h2>
						{produto.valorpromoapi > 0 && produto.valorpromoapi < produto.valor ? (
							<div className="flex items-center gap-2 mt-0.5">
								<span className="text-xs font-bold text-gray-400 line-through">
									{libFloat.strToCurrency(produto.valor)}
								</span>
								<span className="text-sm font-black text-emerald-600">
									{libFloat.strToCurrency(produto.valorpromoapi)}
								</span>
								<span className="rounded-md bg-emerald-100 px-1.5 py-0.5 text-[9px] font-black uppercase text-emerald-700 tracking-wide">
									PROMO
								</span>
							</div>
						) : (
							<p className="text-sm font-bold text-blue-600 mt-0.5">{libFloat.strToCurrency(produto.valor)}</p>
						)}
					</div>
				</div>

				{/* Quantidade do produto principal — some quando tem grade (cada grade vira sua
				    própria linha, sem agregar, mesma regra do sz-foods) */}
				{!hasGrade && (
					<div className="flex flex-shrink-0 items-center gap-0.5 rounded-full border border-gray-200 bg-white p-0.5 shadow-sm">
						<button
							type="button"
							onClick={handleDecrementQtde}
							disabled={Number(quantidade) <= 1}
							className="flex h-7 w-7 items-center justify-center rounded-full bg-blue-600 text-white transition-all hover:bg-blue-700 active:scale-95 disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed disabled:opacity-50"
						>
							<ChevronLeftIcon size={14} />
						</button>
						<input
							ref={qtyInputRef}
							type="text"
							inputMode="numeric"
							pattern="[0-9]*"
							value={quantidade}
							onChange={(e) => handleQuantidadeChange(e.target.value)}
							onBlur={handleQuantidadeBlur}
							onFocus={(e) => e.target.select()}
							onKeyDown={handleQuantidadeKeyDown}
							className="h-7 w-10 rounded-full bg-gray-50 text-center text-sm font-bold tabular-nums text-gray-900 transition-all focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/20"
						/>
						<button
							type="button"
							onClick={handleIncrementQtde}
							className="flex h-7 w-7 items-center justify-center rounded-full bg-blue-600 text-white transition-all hover:bg-blue-700 active:scale-95"
						>
							<ChevronRightIcon size={14} />
						</button>
					</div>
				)}
			</form>

			<div className="min-h-0 flex-1 overflow-y-auto">
				<ProductComplementsSelect
					produto={produto}
					onSelectionChange={setComplementosSelecionados}
					onValidationChange={setComplementosValidos}
					onHasSubitemChange={setHasSubitem}
					onHasGradeChange={setHasGrade}
				/>
			</div>

			<div className="mt-3 flex flex-shrink-0 gap-2 border-t border-gray-100 pt-3">
				<button
					type="button"
					onClick={onCancel}
					className="h-11 rounded-xl border border-gray-200 px-5 text-xs font-bold text-gray-500 hover:bg-gray-50"
				>
					Cancelar
				</button>
				<button
					ref={addButtonRef}
					type="button"
					onClick={handleAdicionar}
					disabled={!complementosValidos}
					className={`h-11 flex-1 rounded-xl text-sm font-bold text-white transition-all ${
						complementosValidos ? "bg-blue-600 hover:bg-blue-700" : "cursor-not-allowed bg-gray-300"
					}`}
				>
					Adicionar à venda
				</button>
			</div>
		</div>
	);
}
